class TrialBuilder extends HTMLElement {
  constructor() {
    super();

    this.requiredCount = parseInt(this.getAttribute('required-count'), 10) || 2;
    this.cartAttributeName = this.getAttribute('cart-attribute-name') || 'grip_trial_program';
    this.cartAttributeValue = this.getAttribute('cart-attribute-value') || 'true';
    this.linePropertyKey = this.getAttribute('line-property-key') || '_wg_trial';
    this.linePropertyValue = this.getAttribute('line-property-value') || 'true';

    this.items = [];
    this.sidebarCart = null;
    this.progressBar = null;
    this.ctaButton = null;
    this.totalEl = null;
    this.errorEl = null;
    this.selectedCountEl = null;
    this.stepCountEl = null;
    this.cartSubtitle = null;
    this.goalText = null;
    this.cartStepElement = null;

    this._variantInventoryCache = new Map();
    this._cartCache = { ts: 0, cart: null };
    this.cartTrialCount = 0;
    this.cartTrialItems = [];
    this.trialLocked = false;
    this._cartSyncTimer = 0;
    this._pendingCart = null;
    this._onCartSync = (event) => {
      const cart = event?.detail?.cart;
      if (cart && typeof cart.item_count === 'number' && Array.isArray(cart.items)) {
        this._pendingCart = cart;
      }
      clearTimeout(this._cartSyncTimer);
      this._cartSyncTimer = setTimeout(() => {
        const nextCart = this._pendingCart;
        this._pendingCart = null;
        this.syncTrialLockFromCart(nextCart);
      }, 150);
    };
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    clearTimeout(this._cartSyncTimer);
    document.removeEventListener('cart:change', this._onCartSync);
    document.removeEventListener('wg:cart:mutated', this._onCartSync);
    document.removeEventListener('kaching:cart:updated', this._onCartSync);
    document.removeEventListener('kachingCart:updated', this._onCartSync);
    window.removeEventListener('pageshow', this._onCartSync);
  }

  async init() {
    this.cacheDOMElements();
    this.initTabs();
    this.initColorFilters();
    this.initCustomDropdowns();
    await this.syncTrialLockFromCart();
    if (!this.trialLocked) this.loadFromLocalStorage();
    this.pruneSelectionToRemaining();
    this.refreshAllDropdownAvailability();
    this.initGripPicker();
    this.initSidebarCart();
    this.initMobileSidebar();
    this.initCtaCloseSidebar();
    this.bindCartSync();
    this.updateUI();
    this.updateAddButtonsState();
    this.updateAddButtonsStateWithCart();
  }

  bindCartSync() {
    document.addEventListener('cart:change', this._onCartSync);
    document.addEventListener('wg:cart:mutated', this._onCartSync);
    document.addEventListener('kaching:cart:updated', this._onCartSync);
    document.addEventListener('kachingCart:updated', this._onCartSync);
    window.addEventListener('pageshow', this._onCartSync);
  }

  isTrialCartItem(item) {
    const value = item?.properties?.[this.linePropertyKey];
    if (value == null || value === '') return false;
    const normalized = String(value).toLowerCase();
    return normalized !== 'false' && normalized !== '0';
  }

  getTrialCountFromCart(cart) {
    if (!cart || !Array.isArray(cart.items)) return 0;
    return cart.items.reduce((sum, item) => {
      if (!this.isTrialCartItem(item)) return sum;
      return sum + (Number(item.quantity) || 0);
    }, 0);
  }

  remainingSlots() {
    return Math.max(0, this.requiredCount - this.cartTrialCount);
  }

  filledCount() {
    return this.cartTrialCount + this.items.length;
  }

  isSelectionComplete() {
    return this.items.length >= 1 && this.items.length <= this.remainingSlots();
  }

  cartTrialVariantIds() {
    return new Set(this.cartTrialItems.map((item) => String(item.variantId)));
  }

  normalizeCartTrialItem(item) {
    const options = Array.isArray(item.options_with_values) ? item.options_with_values : [];
    const sizeOpt = options.find((option) => /size/i.test(option.name || ''));
    const colorOpt = options.find((option) => /colou?r/i.test(option.name || ''));
    const variantParts = String(item.variant_title || '').split(' / ').map((part) => part.trim()).filter(Boolean);
    const image = typeof item.image === 'string'
      ? item.image
      : (item.featured_image?.url || item.featured_image?.src || '');

    return {
      variantId: String(item.variant_id || ''),
      productId: String(item.product_id || ''),
      modelId: String(item.product_id || ''),
      quantity: 1,
      size: sizeOpt?.value || variantParts[1] || '',
      color: colorOpt?.value || variantParts[0] || '',
      name: item.product_title || item.title || '',
      image,
      price: Number(item.original_price || item.price || 0),
      fromCart: true
    };
  }

  getTrialItemsFromCart(cart) {
    if (!cart || !Array.isArray(cart.items)) return [];
    const items = [];
    cart.items.forEach((item) => {
      if (!this.isTrialCartItem(item)) return;
      const normalized = this.normalizeCartTrialItem(item);
      const qty = Number(item.quantity) || 0;
      for (let i = 0; i < qty; i += 1) {
        items.push({ ...normalized });
      }
    });
    return items;
  }

  pruneSelectionToRemaining() {
    const remaining = this.remainingSlots();
    const inCart = this.cartTrialVariantIds();
    this.items = this.items.filter((item) => !inCart.has(String(item.variantId)));
    if (this.items.length > remaining) {
      this.items = this.items.slice(0, remaining);
    }
  }

  applyTrialLockFromCart(cart) {
    this.cartTrialCount = this.getTrialCountFromCart(cart);
    this.cartTrialItems = this.getTrialItemsFromCart(cart);
    this.trialLocked = this.cartTrialCount >= this.requiredCount;
    this.classList.toggle('is-trial-locked', this.trialLocked);

    if (this.trialLocked) {
      this.items = [];
      this.clearLocalStorage();
      this.showError(this.getLocaleString('already-in-cart') || 'Your trial grips are already in the cart.');
      return;
    }

    this.pruneSelectionToRemaining();
  }

  async syncTrialLockFromCart(cartFromEvent) {
    const wasLocked = this.trialLocked;
    const hasFullCart = cartFromEvent && typeof cartFromEvent.item_count === 'number' && Array.isArray(cartFromEvent.items);
    const cart = hasFullCart ? cartFromEvent : await this.fetchCartFresh(0);
    if (hasFullCart) this._cartCache = { ts: Date.now(), cart };
    this.applyTrialLockFromCart(cart);

    if (wasLocked && !this.trialLocked) {
      this.clearError();
    }

    this.updateUI();
    this.updateAddButtonsState();
    this.updateAddButtonsStateWithCart();
  }

  notifyCartUpdated(cart) {
    if (cart) this._cartCache = { ts: Date.now(), cart };
    if (!cart) return;

    document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
      bubbles: true,
      detail: {
        cart,
        baseEvent: 'line-item:change'
      }
    }));
  }

  openStoreCart() {
    if (typeof window.kachingCart?.open === 'function') {
      window.kachingCart.open();
      return;
    }
    if (typeof window.KachingCart?.open === 'function') {
      window.KachingCart.open();
      return;
    }

    document.dispatchEvent(new CustomEvent('kaching:open'));
    window.dispatchEvent(new CustomEvent('kachingCart:open'));

    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.show === 'function') {
      drawer.show();
    } else if (drawer && typeof drawer.open === 'function') {
      drawer.open();
    }
  }

  cacheDOMElements() {
    this.sidebarCart = this.querySelector('.bundle-sidebar-cart');
    this.progressBar = this.querySelector('.bundle-progress-fill');
    this.ctaButton = this.querySelector('[data-cart-cta]');
    this.ctaLabel = this.querySelector('[data-cta-label]');
    this.totalEl = this.querySelector('[data-total]');
    this.creditEl = this.querySelector('[data-credit]');
    this.stepTitleEl = this.querySelector('[data-step-title]');
    this.gripModal = this.querySelector('x-modal.trial-grip-modal');
    this.errorEl = this.querySelector('[data-trial-error]');
    this.selectedCountEl = this.querySelector('[data-selected-count]');
    this.stepCountEl = this.querySelector('[data-step-count]');
    this.cartSubtitle = this.querySelector('.bundle-cart-subtitle');
    this.goalText = this.querySelector('[data-goal-text]');
    this.cartStepElement = this.querySelector('[data-cart-step]');
  }

  getLocaleString(name) {
    return this.getAttribute(`data-locale-${name}`) || '';
  }

  routesRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message || '';
    this.errorEl.hidden = !message;
  }

  clearError() {
    this.showError('');
  }

  getSelectedContext(card) {
    const select = card.querySelector('.bundle-size-select');
    const addBtn = card.querySelector('.bundle-add-btn');
    const variantId = select
      ? select.value
      : (addBtn?.getAttribute('data-variant-id') || card.getAttribute('data-variant-id'));

    let size = card.getAttribute('data-size') || '';
    if (select && select.selectedOptions && select.selectedOptions.length > 0) {
      const selectedOption = select.selectedOptions[0];
      size = selectedOption.getAttribute('data-size') || selectedOption.textContent.trim() || size;
    }

    let inventoryQuantityAttr = addBtn?.getAttribute('data-max-inventory');
    if (select && select.selectedOptions && select.selectedOptions.length > 0) {
      const optMax = select.selectedOptions[0].getAttribute('data-max-inventory');
      if (optMax !== null && optMax !== undefined) inventoryQuantityAttr = optMax;
    }

    return {
      variantId: String(variantId || ''),
      size,
      modelId: card.getAttribute('data-model-id') || card.getAttribute('data-product-id') || '',
      productId: card.getAttribute('data-product-id') || '',
      color: card.getAttribute('data-color') || card.querySelector('.bundle-product-variant')?.textContent.trim() || '',
      name: card.querySelector('.bundle-product-name')?.textContent.trim() || '',
      image: card.querySelector('.bundle-product-image img')?.src || '',
      price: parseInt(card.getAttribute('data-original-price'), 10) || 0,
      available: addBtn?.getAttribute('data-available'),
      inventoryQuantityAttr
    };
  }

  initTabs() {
    const tabs = this.querySelectorAll('.bundle-tab');

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        const tabId = tab.getAttribute('data-tab');
        const tabStep = parseInt(tab.getAttribute('data-step'), 10);

        const stepTabs = this.querySelectorAll(`.bundle-tab[data-step="${tabStep}"]`);
        stepTabs.forEach((t) => t.classList.remove('active'));
        tab.classList.add('active');

        const stepElement = this.querySelector(`.bundle-step[data-step="${tabStep}"]`);
        if (!stepElement) return;

        const stepTabContents = stepElement.querySelectorAll('.bundle-product-grid');
        stepTabContents.forEach((content) => content.classList.remove('active'));

        const activeContent = stepElement.querySelector(`.bundle-product-grid[data-tab-content="${tabId}"]`);
        if (activeContent) {
          activeContent.classList.add('active');
        }

        const colorFilters = this.querySelectorAll(`.bundle-color-filter-wrapper[data-step="${tabStep}"]`);
        colorFilters.forEach((filter) => {
          const filterTabContent = filter.getAttribute('data-tab-content');
          if (filterTabContent === tabId) {
            filter.style.display = 'block';
          } else if (filterTabContent) {
            filter.style.display = 'none';
          }
        });

        this.applyColorFilters();
        this.updateClearButtonVisibility();
      });
    });
  }

  initColorFilters() {
    const colorSwatches = this.querySelectorAll('.bundle-color-swatch');

    colorSwatches.forEach((swatch) => {
      swatch.addEventListener('click', () => {
        swatch.classList.toggle('active');
        this.applyColorFilters();
        this.updateClearButtonVisibility();
      });
    });

    const clearButtons = this.querySelectorAll('.bundle-color-filter-clear');
    clearButtons.forEach((button) => {
      button.addEventListener('click', () => {
        this.clearColorFilters(button);
      });
    });

    setTimeout(() => {
      this.applyColorFilters();
      this.updateClearButtonVisibility();
    }, 100);
  }

  clearColorFilters(clearButton) {
    const filterWrapper = clearButton.closest('.bundle-color-filter-wrapper');
    if (!filterWrapper) return;

    filterWrapper.querySelectorAll('.bundle-color-swatch.active').forEach((swatch) => {
      swatch.classList.remove('active');
    });

    this.applyColorFilters();
    this.updateClearButtonVisibility();
  }

  updateClearButtonVisibility() {
    this.querySelectorAll('.bundle-color-filter-clear').forEach((button) => {
      const filterWrapper = button.closest('.bundle-color-filter-wrapper');
      if (!filterWrapper) return;
      const hasActiveSwatches = filterWrapper.querySelectorAll('.bundle-color-swatch.active').length > 0;
      button.style.display = hasActiveSwatches ? 'block' : 'none';
    });
  }

  applyColorFilters() {
    const currentStepElement = this.querySelector('.bundle-step[data-step="1"]');
    if (!currentStepElement) return;

    const activeGrids = currentStepElement.querySelectorAll('.bundle-product-grid.active');

    activeGrids.forEach((grid) => {
      const tabContentId = grid.getAttribute('data-tab-content');
      const productId = tabContentId ? tabContentId.replace('product-', '') : null;

      let currentStepFilter = null;
      if (productId) {
        currentStepFilter = this.querySelector(`.bundle-color-filter-wrapper[data-product-id="${productId}"][data-step="1"]`);
      } else {
        currentStepFilter = this.querySelector('.bundle-color-filter-wrapper[data-step="1"]:not([data-product-id])');
      }

      if (!currentStepFilter) {
        grid.querySelectorAll('.bundle-product-card').forEach((card) => {
          card.style.display = '';
        });
        return;
      }

      const selectedColors = Array.from(currentStepFilter.querySelectorAll('.bundle-color-swatch.active'))
        .map((swatch) => swatch.getAttribute('data-color-slug'))
        .filter(Boolean);

      grid.querySelectorAll('.bundle-product-card').forEach((card) => {
        const cardColors = card.getAttribute('data-colors');

        if (selectedColors.length === 0) {
          card.style.display = '';
        } else if (cardColors) {
          const cardColorArray = cardColors.split(' ').filter(Boolean);
          const hasMatch = selectedColors.some((selectedColor) => cardColorArray.includes(selectedColor));
          card.style.display = hasMatch ? '' : 'none';
        } else {
          card.style.display = 'none';
        }
      });
    });
  }

  initCustomDropdowns() {
    const dropdowns = this.querySelectorAll('.custom-dropdown');

    dropdowns.forEach((dropdown) => {
      const select = dropdown.previousElementSibling;
      const selectedEl = dropdown.querySelector('.custom-dropdown-selected');
      const optionEls = dropdown.querySelectorAll('.custom-dropdown-option');
      const textEl = dropdown.querySelector('.custom-dropdown-text');
      const productCard = dropdown.closest('.bundle-product-card');

      selectedEl.addEventListener('click', (e) => {
        e.stopPropagation();
        this.querySelectorAll('.custom-dropdown').forEach((dd) => {
          if (dd !== dropdown) dd.classList.remove('open');
        });
        dropdown.classList.toggle('open');
      });

      optionEls.forEach((optionEl) => {
        optionEl.addEventListener('click', () => {
          if (optionEl.classList.contains('is-disabled') || optionEl.dataset.disabled === '1' || optionEl.getAttribute('aria-disabled') === 'true') {
            return;
          }

          const value = optionEl.getAttribute('data-value');
          const text = optionEl.textContent.trim();
          const priceAttr = optionEl.getAttribute('data-price');
          const maxInventoryAttr = optionEl.getAttribute('data-max-inventory');
          const sizeAttr = optionEl.getAttribute('data-size') || text;

          if (select) select.value = value;
          if (textEl) textEl.textContent = text;

          optionEls.forEach((opt) => opt.classList.remove('selected'));
          optionEl.classList.add('selected');
          dropdown.classList.remove('open');

          productCard.setAttribute('data-size', sizeAttr);
          productCard.setAttribute('data-variant-id', value);

          const addButton = productCard.querySelector('.bundle-add-btn');
          const availableAttr = optionEl.dataset.available;
          if (addButton && availableAttr != null) {
            addButton.setAttribute('data-available', availableAttr);
          }
          if (addButton && value) {
            addButton.setAttribute('data-variant-id', value);
            if (maxInventoryAttr) addButton.setAttribute('data-max-inventory', maxInventoryAttr);
          }

          if (priceAttr && productCard) {
            const originalPrice = parseInt(priceAttr, 10) || 0;
            productCard.setAttribute('data-original-price', String(originalPrice));
            const priceCurrentEl = productCard.querySelector('.price-current');
            if (priceCurrentEl) priceCurrentEl.textContent = this.formatPrice(originalPrice / 100);
          }

          this.clearError();
          this.updateCardStates();
          this.updateAddButtonsState();
          this.updateAddButtonsStateWithCart();
        });
      });
    });

    document.addEventListener('click', () => {
      this.querySelectorAll('.custom-dropdown').forEach((dd) => dd.classList.remove('open'));
    });
  }

  applySelectionFromSelect(productCard) {
    const select = productCard.querySelector('.bundle-size-select');
    const dropdown = productCard.querySelector('.custom-dropdown');
    if (!select) return;

    const selectedOpt = select.options[select.selectedIndex];
    if (!selectedOpt) return;

    const value = selectedOpt.value;
    const priceAttr = selectedOpt.getAttribute('data-price');
    const maxInvAttr = selectedOpt.getAttribute('data-max-inventory');
    const availableAttr = selectedOpt.dataset.available;
    const sizeAttr = selectedOpt.getAttribute('data-size') || selectedOpt.textContent.trim();

    productCard.setAttribute('data-variant-id', value);
    productCard.setAttribute('data-size', sizeAttr);

    if (dropdown) {
      const textEl = dropdown.querySelector('.custom-dropdown-text');
      const optionDivs = Array.from(dropdown.querySelectorAll('.custom-dropdown-option'));
      optionDivs.forEach((d) => d.classList.toggle('selected', d.getAttribute('data-value') === value));
      const matchDiv = optionDivs.find((d) => d.getAttribute('data-value') === value);
      if (textEl) {
        const txt = (matchDiv ? matchDiv.textContent : selectedOpt.textContent).trim();
        textEl.textContent = txt;
      }
    }

    const addBtn = productCard.querySelector('.bundle-add-btn');
    if (addBtn) {
      addBtn.setAttribute('data-variant-id', value);
      if (maxInvAttr != null) addBtn.setAttribute('data-max-inventory', maxInvAttr);
      if (availableAttr != null) addBtn.setAttribute('data-available', availableAttr);
    }

    if (priceAttr) {
      const original = parseInt(priceAttr, 10) || 0;
      productCard.setAttribute('data-original-price', String(original));
      const priceCurrentEl = productCard.querySelector('.price-current');
      if (priceCurrentEl) priceCurrentEl.textContent = this.formatPrice(original / 100);
    }
  }

  refreshAllDropdownAvailability(cart = this.getCartCached()) {
    let anySelectionChanged = false;
    const cards = Array.from(this.querySelectorAll('.bundle-product-card'));

    for (const card of cards) {
      const select = card.querySelector('.bundle-size-select');
      const dropdown = card.querySelector('.custom-dropdown');
      if (!select) continue;

      const optionDivs = dropdown ? Array.from(dropdown.querySelectorAll('.custom-dropdown-option')) : [];
      const nativeOptions = Array.from(select.options);

      for (const opt of nativeOptions) {
        const variantId = String(opt.value);
        const availableFlag = opt.dataset.available === 'true';
        const maxInv = parseInt(opt.getAttribute('data-max-inventory') || '', 10);
        const inTrial = this.getVariantQuantityInTrial(variantId);
        const inCart = this.getVariantQtyInCart(cart, variantId);
        const hasLimit = availableFlag && Number.isFinite(maxInv) && maxInv > 0;
        const remaining = hasLimit ? (maxInv - inCart - inTrial) : 999999;
        const selectable = availableFlag && remaining > 0;

        opt.disabled = !selectable;

        const div = optionDivs.find((d) => String(d.getAttribute('data-value')) === variantId);
        if (div) {
          div.classList.toggle('is-disabled', !selectable);
          div.setAttribute('aria-disabled', selectable ? 'false' : 'true');
          div.dataset.disabled = selectable ? '0' : '1';
        }
      }

      const currentOpt = nativeOptions.find((o) => o.value === select.value);
      if (currentOpt && currentOpt.disabled) {
        const firstOk = nativeOptions.find((o) => !o.disabled);
        if (firstOk) {
          select.value = firstOk.value;
          anySelectionChanged = true;
        }
      }

      this.applySelectionFromSelect(card);
    }

    return anySelectionChanged;
  }

  getVariantQuantityInTrial(variantId) {
    return this.items.reduce((total, item) => {
      return String(item.variantId) === String(variantId) ? total + item.quantity : total;
    }, 0);
  }

  initGripPicker() {
    this.querySelectorAll('.trial-grip-select').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (btn.disabled) return;
        this.openGripPicker(btn.getAttribute('data-product-id'));
      });
    });

    this.querySelectorAll('.trial-grip-picker').forEach((picker) => {
      picker.querySelectorAll('[data-size-option]').forEach((button) => {
        button.addEventListener('click', () => {
          picker.querySelectorAll('[data-size-option]').forEach((el) => el.classList.remove('is-selected'));
          button.classList.add('is-selected');
          this.syncGripPicker(picker);
        });
      });

      picker.querySelectorAll('[data-color-option]').forEach((button) => {
        button.addEventListener('click', () => {
          if (button.classList.contains('is-disabled')) return;
          picker.querySelectorAll('[data-color-option]').forEach((el) => el.classList.remove('is-selected'));
          button.classList.add('is-selected');
          this.syncGripPicker(picker);
        });
      });

      const addButton = picker.querySelector('[data-add-grip]');
      if (addButton) {
        addButton.addEventListener('click', () => {
          this.confirmGripPicker(picker);
        });
      }

      this.syncGripPicker(picker);
    });

    if (this.gripModal) {
      this.gripModal.addEventListener('dialog:after-show', () => {
        requestAnimationFrame(() => this.syncPickerBlurbs());
      });
    }

    window.addEventListener('resize', () => {
      if (this.gripModal && this.gripModal.hasAttribute('open')) this.syncPickerBlurbs();
    });
  }

  gripPickers() {
    const root = this.gripModal || this;
    return root.querySelectorAll('.trial-grip-picker');
  }

  openGripPicker(productId) {
    if (!this.gripModal) return;
    this.gripPickers().forEach((picker) => {
      const isMatch = String(picker.getAttribute('data-product-id')) === String(productId);
      picker.hidden = !isMatch;
      if (isMatch) {
        this.collapsePickerBlurb(picker);
        this.syncGripPicker(picker);
      }
    });
    this.updatePickerAddLabel();
    requestAnimationFrame(() => this.syncPickerBlurbs());
  }

  collapsePickerBlurb(picker) {
    const blurb = picker.querySelector('[data-picker-blurb]');
    const button = picker.querySelector('[data-read-more]');
    if (!blurb || !button) return;
    blurb.classList.remove('is-expanded');
    button.setAttribute('aria-expanded', 'false');
    const moreLabel = button.getAttribute('data-label-more');
    if (moreLabel) button.textContent = moreLabel;
  }

  syncPickerBlurbs() {
    this.gripPickers().forEach((picker) => {
      if (!picker.hidden) this.syncPickerBlurb(picker);
    });
  }

  syncPickerBlurb(picker) {
    const blurb = picker.querySelector('[data-picker-blurb]');
    const button = picker.querySelector('[data-read-more]');
    if (!blurb || !button) return;

    if (!button.dataset.bound) {
      button.dataset.bound = 'true';
      button.addEventListener('click', () => {
        const expanded = blurb.classList.toggle('is-expanded');
        button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        button.textContent = expanded
          ? (button.getAttribute('data-label-less') || 'Read less')
          : (button.getAttribute('data-label-more') || 'Read more');
      });
    }

    if (blurb.classList.contains('is-expanded')) {
      button.hidden = false;
      return;
    }

    button.hidden = !this.blurbOverflows(blurb);
  }

  blurbOverflows(blurb) {
    const width = blurb.getBoundingClientRect().width;
    if (width <= 0) return false;

    const clone = blurb.cloneNode(true);
    clone.classList.add('is-expanded');
    clone.setAttribute('aria-hidden', 'true');
    clone.style.position = 'absolute';
    clone.style.visibility = 'hidden';
    clone.style.pointerEvents = 'none';
    clone.style.width = `${width}px`;
    clone.style.height = 'auto';
    clone.style.maxHeight = 'none';
    blurb.parentElement.appendChild(clone);
    const overflows = clone.scrollHeight > blurb.clientHeight + 1;
    clone.remove();
    return overflows;
  }

  pickerVariants(picker) {
    if (!picker._variantData) {
      const script = picker.querySelector('.trial-grip-variants');
      try {
        picker._variantData = script ? JSON.parse(script.textContent) : [];
      } catch (error) {
        picker._variantData = [];
      }
    }
    return picker._variantData;
  }

  selectedPickerValue(picker, selector) {
    return picker.querySelector(`${selector}.is-selected`)?.getAttribute('data-value') || '';
  }

  variantMatches(variant, size, color) {
    const sizeOk = !size || variant.size === size;
    const colorOk = !color || variant.color === color;
    return sizeOk && colorOk;
  }

  getPickerVariant(picker) {
    const variants = this.pickerVariants(picker);
    const size = this.selectedPickerValue(picker, '[data-size-option]');
    const color = this.selectedPickerValue(picker, '[data-color-option]');
    return variants.find((variant) => this.variantMatches(variant, size, color)) || null;
  }

  syncGripPicker(picker) {
    const variants = this.pickerVariants(picker);
    const size = this.selectedPickerValue(picker, '[data-size-option]');
    const colorButtons = picker.querySelectorAll('[data-color-option]');

    colorButtons.forEach((button) => {
      const color = button.getAttribute('data-value') || '';
      const match = variants.some((variant) => this.variantMatches(variant, size, color) && variant.available);
      button.classList.toggle('is-disabled', !match);
      button.disabled = !match;
      if (!match && button.classList.contains('is-selected')) {
        button.classList.remove('is-selected');
      }
    });

    if (!picker.querySelector('[data-color-option].is-selected')) {
      const first = Array.from(colorButtons).find((button) => !button.disabled);
      if (first) first.classList.add('is-selected');
    }

    const selectedColor = this.selectedPickerValue(picker, '[data-color-option]');
    const variant = this.getPickerVariant(picker);
    const chosen = picker.querySelector('[data-chosen-color]');
    if (chosen) chosen.textContent = selectedColor;

    picker.querySelectorAll('[data-size-option]').forEach((button) => {
      const size = button.getAttribute('data-value') || '';
      const match = variants.find((item) => this.variantMatches(item, size, selectedColor));
      button.classList.toggle('is-taken', this.isVariantTaken(match));
    });

    picker.querySelectorAll('[data-color-option]').forEach((button) => {
      const color = button.getAttribute('data-value') || '';
      const match = variants.find((item) => this.variantMatches(item, size, color));
      const taken = this.isVariantTaken(match);
      button.classList.toggle('is-taken', taken);
      const swatch = button.querySelector('.color-swatch');
      if (swatch) swatch.classList.toggle('is-disabled', taken || button.disabled);
    });

    if (variant && variant.image) {
      const image = picker.querySelector('.trial-grip-picker__image');
      if (image && image.getAttribute('src') !== variant.image) {
        image.src = variant.image;
        image.removeAttribute('srcset');
      }
    }

    const taken = this.isVariantTaken(variant);
    const note = picker.querySelector('[data-picker-note]');
    const takenLabel = this.getLocaleString('already-selected') || 'This grip is already in your trial.';
    if (note) {
      note.hidden = !taken;
      note.textContent = taken ? takenLabel : '';
    }

    const addButton = picker.querySelector('[data-add-grip]');
    if (addButton) {
      addButton.classList.toggle('is-taken', taken);
      addButton.disabled = !variant || !variant.available || taken || this.trialLocked || this.items.length >= this.remainingSlots();
      if (taken) {
        addButton.textContent = takenLabel;
      } else if (addButton.dataset.defaultLabel) {
        addButton.textContent = addButton.dataset.defaultLabel;
      }
    }
  }

  isVariantTaken(variant) {
    if (!variant) return false;
    const id = String(variant.id);
    return this.cartTrialVariantIds().has(id) || this.items.some((item) => String(item.variantId) === id);
  }

  updatePickerAddLabel() {
    if (!this.gripModal) return;
    const template = this.gripModal.getAttribute('data-add-label') || 'Add as grip #__COUNT__';
    const count = Math.min(this.filledCount() + 1, this.requiredCount);
    const label = template.replace('__COUNT__', String(count));
    this.gripPickers().forEach((picker) => {
      const button = picker.querySelector('[data-add-grip]');
      if (button) button.dataset.defaultLabel = label;
      this.syncGripPicker(picker);
    });
  }

  updateStepHeading() {
    const next = Math.min(this.filledCount() + 1, this.requiredCount);
    this.querySelectorAll('[data-step-index]').forEach((el) => {
      el.textContent = String(this.filledCount() >= this.requiredCount ? this.requiredCount : next);
    });
    if (!this.stepTitleEl) return;
    const first = this.stepTitleEl.getAttribute('data-title-first') || '';
    const later = this.stepTitleEl.getAttribute('data-title-next') || first;
    this.stepTitleEl.textContent = next <= 1 ? first : later;
  }

  async confirmGripPicker(picker) {
    const variant = this.getPickerVariant(picker);
    if (!variant || !variant.available) return;

    if (this.trialLocked || this.items.length >= this.remainingSlots()) {
      this.showError(
        this.trialLocked
          ? (this.getLocaleString('already-in-cart') || 'Your trial grips are already in the cart.')
          : (this.getLocaleString('trial-full') || 'Remove a grip before adding another.')
      );
      return;
    }

    const inCart = this.cartTrialVariantIds();
    if (inCart.has(String(variant.id)) || this.items.some((item) => String(item.variantId) === String(variant.id))) {
      this.showError(this.getLocaleString('already-selected') || 'This grip is already in your trial.');
      return;
    }

    const inventoryAttr = variant.inventory == null ? '' : String(variant.inventory);
    const inv = await this.fetchVariantInventoryLimit(variant.id, inventoryAttr);
    const cart = await this.fetchCartFresh();
    const alreadyInCart = this.getVariantQtyInCart(cart, variant.id);
    const alreadyInTrial = this.getVariantQuantityInTrial(variant.id);
    const availableQuantity = inv.hasLimit
      ? (inv.maxInventory - alreadyInCart - alreadyInTrial)
      : 999999;

    if (inv.hasLimit && availableQuantity < 1) {
      this.showError(this.getLocaleString('trial-full') || 'Remove a grip before adding another.');
      return;
    }

    const card = this.querySelector(`.bundle-product-card[data-product-id="${picker.getAttribute('data-product-id')}"]`);
    this.addItem({
      variantId: String(variant.id),
      productId: String(picker.getAttribute('data-product-id') || ''),
      modelId: String(picker.getAttribute('data-product-id') || ''),
      quantity: 1,
      size: variant.size || '',
      color: variant.color || '',
      name: picker.getAttribute('data-product-title') || card?.querySelector('.bundle-product-name')?.textContent.trim() || '',
      image: variant.image || card?.querySelector('img')?.src || '',
      price: Number(variant.price) || 0
    });

    if (this.gripModal && typeof this.gripModal.hide === 'function') {
      this.gripModal.hide();
    }
  }

  addItem(item) {
    if (this.trialLocked || this.items.length >= this.remainingSlots()) return;
    this.items.push(item);
    this.clearError();
    this.updateUI();
    this.refreshAllDropdownAvailability(this.getCartCached());
    this.updateAddButtonsState();
    this.updateAddButtonsStateWithCart();
    this.saveToLocalStorage();
  }

  removeItem(variantId) {
    this.items = this.items.filter((item) => String(item.variantId) !== String(variantId));
    this.clearError();
    this.updateUI();
    this.refreshAllDropdownAvailability(this.getCartCached());
    this.updateAddButtonsState();
    this.updateAddButtonsStateWithCart();
    this.saveToLocalStorage();
  }

  updateUI() {
    this.updateSidebarCart();
    this.updateProgress();
    this.updateTotals();
    this.updateButtonState();
    this.updateStepHeading();
    this.updateCardStates();
    this.updateCompletionState();
    this.updatePickerAddLabel();
  }

  updateCardStates() {
    const selectedProductIds = new Set([
      ...this.items.map((item) => String(item.productId)),
      ...this.cartTrialItems.map((item) => String(item.productId))
    ]);
    const atCapacity = this.trialLocked || this.items.length >= this.remainingSlots();
    const fullText = this.trialLocked
      ? (this.getLocaleString('already-in-cart') || 'Your trial grips are already in the cart.')
      : (this.getLocaleString('trial-full') || 'Remove a grip before adding another.');
    const selectedText = this.getLocaleString('already-selected') || 'This grip is already in your trial.';

    this.querySelectorAll('.bundle-product-card').forEach((card) => {
      const productId = String(card.getAttribute('data-product-id') || '');
      const isSelected = selectedProductIds.has(productId);
      const note = card.querySelector('[data-card-note]');

      card.classList.toggle('is-selected', isSelected);

      if (!note) return;

      if (atCapacity && isSelected) {
        note.textContent = selectedText;
        note.hidden = false;
        note.classList.add('is-chosen');
      } else if (atCapacity) {
        note.textContent = fullText;
        note.hidden = false;
        note.classList.remove('is-chosen');
      } else {
        note.textContent = '';
        note.hidden = true;
        note.classList.remove('is-chosen');
      }
    });
  }

  updateCompletionState() {
    const complete = this.trialLocked || this.filledCount() >= 1;

    if (this.cartStepElement) {
      this.cartStepElement.classList.toggle('completed', complete);
      this.cartStepElement.classList.toggle('active', !complete);
    }

    if (this.cartSubtitle && this.goalText) {
      this.cartSubtitle.style.display = complete ? 'none' : 'block';
      this.goalText.hidden = !complete;
      this.goalText.style.display = complete ? 'block' : 'none';
    }
  }

  updateSidebarCart() {
    const container = this.querySelector('[data-items-container]');
    if (!container) return;

    container.querySelectorAll('[data-variant-id]').forEach((item) => item.remove());

    const placeholders = container.querySelectorAll('[data-placeholder]');
    const sidebarItems = this.trialLocked ? this.cartTrialItems : [...this.cartTrialItems, ...this.items];
    sidebarItems.forEach((item) => {
      const itemElement = this.renderCartItem(item, { removable: !item.fromCart });
      if (placeholders[0]) {
        container.insertBefore(itemElement, placeholders[0]);
      } else {
        container.appendChild(itemElement);
      }
    });

    const emptySlots = this.trialLocked ? 0 : Math.max(0, this.requiredCount - sidebarItems.length);
    placeholders.forEach((placeholder, index) => {
      placeholder.style.display = index < emptySlots ? 'flex' : 'none';
    });

    let slotNumber = 1;
    let upcomingEmpty = false;
    container.querySelectorAll('.trial-slot').forEach((slot) => {
      if (slot.style.display === 'none') return;
      const indexEl = slot.querySelector('[data-slot-index]');
      const labelEl = slot.querySelector('[data-slot-label]');
      if (indexEl) indexEl.textContent = String(slotNumber);
      if (labelEl) {
        const label = container.getAttribute(`data-slot-label-${slotNumber}`) || '';
        labelEl.textContent = label;
        labelEl.hidden = label === '';
      }
      const isEmpty = slot.classList.contains('trial-slot--empty');
      slot.classList.toggle('is-later', isEmpty && upcomingEmpty);
      if (isEmpty) upcomingEmpty = true;
      slotNumber += 1;
    });

    if (window.innerWidth < 1024 && this.sidebarCart?.classList.contains('expanded')) {
      this.drawerPanels().forEach((panel) => {
        panel.style.height = 'auto';
      });
    }

    this.querySelectorAll('[data-selected-count]').forEach((el) => {
      el.textContent = String(this.filledCount());
    });
    if (this.stepCountEl) this.stepCountEl.textContent = String(this.filledCount());
  }

  renderCartItem(item, { removable = true } = {}) {
    const div = document.createElement('div');
    div.className = 'trial-slot';
    div.setAttribute('data-variant-id', item.variantId);
    if (item.fromCart) div.setAttribute('data-from-cart', 'true');

    const details = [item.size, item.color].filter(Boolean).join(' · ');

    div.innerHTML = `
      <div class="trial-slot__head">
        <span class="trial-slot__index" data-slot-index></span>
        <span class="trial-slot__label" data-slot-label hidden></span>
      </div>
      <div class="trial-slot__body">
        <div class="trial-slot__media">
          ${item.image ? `<img src="${this.escapeHtml(item.image)}" alt="${this.escapeHtml(item.name)}">` : ''}
        </div>
        <div class="trial-slot__info">
          <p class="trial-slot__name">${this.escapeHtml(item.name)}</p>
          ${details ? `<p class="trial-slot__meta">${this.escapeHtml(details)}</p>` : ''}
        </div>
        <p class="trial-slot__price">${this.escapeHtml(this.formatPrice((Number(item.price) || 0) / 100))}</p>
        ${removable ? `
          <button class="trial-slot__remove" type="button" aria-label="${this.escapeHtml(this.getLocaleString('remove-item') || 'Remove item')}">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0.878125 11.0896L0 10.2115L4.66667 5.54479L0 0.878125L0.878125 0L5.54479 4.66667L10.2115 0L11.0896 0.878125L6.42292 5.54479L11.0896 10.2115L10.2115 11.0896L5.54479 6.42292L0.878125 11.0896Z" fill="currentColor"/>
            </svg>
          </button>
        ` : ''}
      </div>
    `;

    const removeBtn = div.querySelector('.trial-slot__remove');
    if (removeBtn) {
      removeBtn.addEventListener('click', () => {
        this.removeItem(item.variantId);
      });
    }

    return div;
  }

  updateProgress() {
    const progress = this.requiredCount > 0
      ? Math.min((this.filledCount() / this.requiredCount) * 100, 100)
      : 0;
    if (this.progressBar) this.progressBar.style.width = `${progress}%`;
  }

  updateTotals() {
    const sidebarItems = this.trialLocked ? this.cartTrialItems : [...this.cartTrialItems, ...this.items];
    const total = sidebarItems.reduce((sum, item) => sum + (item.price * (item.quantity || 1)), 0);
    const formatted = this.formatPrice(total / 100);
    if (this.totalEl) this.totalEl.textContent = formatted;
    if (this.creditEl) this.creditEl.textContent = formatted;
  }

  formatPrice(amount) {
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    const locale = (window.Shopify && window.Shopify.locale) || 'en-US';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency
    }).format(amount);
  }

  updateButtonState() {
    if (!this.ctaButton) return;
    const label = this.ctaLabel || this.ctaButton;
    if (this.trialLocked) {
      this.ctaButton.disabled = true;
      label.textContent = this.getLocaleString('already-in-cart-cta') || 'TRIAL IN CART';
      return;
    }
    const complete = this.isSelectionComplete();
    this.ctaButton.disabled = !complete;
    label.textContent = this.ctaButton.getAttribute('data-add-to-cart-text') || this.getLocaleString('add-to-trial') || 'Continue';
  }

  hasInventoryLimit(inventoryQuantity) {
    if (inventoryQuantity === null || inventoryQuantity === undefined || inventoryQuantity === '') return false;
    const quantity = parseInt(inventoryQuantity, 10);
    return !isNaN(quantity) && quantity >= 0;
  }

  getEffectiveMaxInventory(inventoryQuantity) {
    if (!this.hasInventoryLimit(inventoryQuantity)) return 999999;
    return parseInt(inventoryQuantity, 10);
  }

  getCartCached() {
    return this._cartCache?.cart || null;
  }

  updateAddButtonsState() {
    const atCapacity = this.trialLocked || this.items.length >= this.remainingSlots();

    this.querySelectorAll('.trial-grip-select').forEach((btn) => {
      const card = btn.closest('.bundle-product-card');
      const unavailable = card?.getAttribute('data-available') === 'false';
      btn.disabled = this.trialLocked || unavailable || atCapacity;
      btn.classList.toggle('disabled', btn.disabled);
    });

    this.gripPickers().forEach((picker) => this.syncGripPicker(picker));
    this.updateCardStates();
  }

  async updateAddButtonsStateWithCart() {
    await this.fetchCartFresh();
    this.updateAddButtonsState();
  }

  initSidebarCart() {
    if (this.ctaButton) {
      this.ctaButton.addEventListener('click', () => {
        this.handleCTAClick();
      });
    }
    this.updateUI();
  }

  handleCTAClick() {
    if (this.trialLocked) {
      this.showError(this.getLocaleString('already-in-cart') || 'Your trial grips are already in the cart.');
      return;
    }
    if (!this.isSelectionComplete()) {
      this.showError(this.getLocaleString('please-select') || 'Select 1 or 2 grips to start your trial.');
      return;
    }
    this.addTrialToCart();
  }

  async addTrialToCart() {
    if (this.trialLocked) {
      this.showError(this.getLocaleString('already-in-cart') || 'Your trial grips are already in the cart.');
      return;
    }
    if (!this.isSelectionComplete()) {
      this.showError(this.getLocaleString('please-select') || 'Select 1 or 2 grips to start your trial.');
      return;
    }

    const existingCart = await this.fetchCartFresh(0);
    const existingCount = this.getTrialCountFromCart(existingCart);
    this.cartTrialCount = existingCount;
    this.cartTrialItems = this.getTrialItemsFromCart(existingCart);
    this.pruneSelectionToRemaining();

    if (existingCount >= this.requiredCount) {
      this.applyTrialLockFromCart(existingCart);
      this.updateUI();
      this.updateAddButtonsState();
      return;
    }

    if (this.items.length < 1 || existingCount + this.items.length > this.requiredCount) {
      this.showError(this.getLocaleString('please-select') || 'Select 1 or 2 grips to start your trial.');
      this.updateUI();
      this.updateAddButtonsState();
      return;
    }

    const root = this.routesRoot();

    try {
      if (this.ctaButton) {
        this.ctaButton.disabled = true;
        const label = this.ctaLabel || this.ctaButton;
        label.textContent = this.getLocaleString('adding') || 'ADDING...';
      }

      const addResponse = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          items: this.items.map((item) => ({
            id: Number(item.variantId),
            quantity: 1,
            properties: {
              [this.linePropertyKey]: this.linePropertyValue
            }
          }))
        })
      });

      if (!addResponse.ok) {
        throw new Error('Failed to add to cart');
      }

      const attributePayload = {
        attributes: {
          [this.cartAttributeName]: this.cartAttributeValue
        }
      };

      const updateResponse = await fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        credentials: 'same-origin',
        body: JSON.stringify(attributePayload)
      });

      let cart = null;
      if (updateResponse.ok) {
        cart = await updateResponse.json();
      } else {
        cart = await this.fetchCartFresh(0);
      }

      this.clearLocalStorage();
      this.items = [];
      this.applyTrialLockFromCart(cart);
      this.notifyCartUpdated(cart);
      this.updateUI();
      this.updateAddButtonsState();
      this.openStoreCart();
    } catch (error) {
      console.error('Error adding trial to cart:', error);
      this.showError(this.getLocaleString('failed-to-add') || 'Failed to add items to cart. Please try again.');
      this.updateButtonState();
    }
  }

  saveToLocalStorage() {
    try {
      localStorage.setItem('wedgeguys_trial_builder', JSON.stringify({
        items: this.items,
        timestamp: Date.now()
      }));
    } catch (error) {
      console.error('Failed to save trial selection:', error);
    }
  }

  loadFromLocalStorage() {
    try {
      const savedData = localStorage.getItem('wedgeguys_trial_builder');
      if (!savedData) return;

      const trialData = JSON.parse(savedData);
      const dayInMs = 24 * 60 * 60 * 1000;
      if (Date.now() - trialData.timestamp >= dayInMs) {
        localStorage.removeItem('wedgeguys_trial_builder');
        return;
      }

      const restored = Array.isArray(trialData.items) ? trialData.items : [];
      const inCart = this.cartTrialVariantIds();
      const valid = [];
      restored.forEach((item) => {
        if (!item || !item.variantId) return;
        if (valid.length >= this.remainingSlots()) return;
        if (inCart.has(String(item.variantId))) return;
        if (valid.some((existing) => String(existing.variantId) === String(item.variantId))) {
          return;
        }
        valid.push({
          variantId: String(item.variantId),
          productId: String(item.productId || ''),
          modelId: String(item.modelId),
          quantity: 1,
          size: item.size || '',
          color: item.color || '',
          name: item.name || '',
          image: item.image || '',
          price: parseInt(item.price, 10) || 0
        });
      });
      this.items = valid;
    } catch (error) {
      console.error('Failed to load trial selection:', error);
    }
  }

  clearLocalStorage() {
    try {
      localStorage.removeItem('wedgeguys_trial_builder');
    } catch (error) {
      console.error('Failed to clear trial selection:', error);
    }
  }

  drawerPanels() {
    if (!this.sidebarCart) return [];
    return this.sidebarCart.querySelectorAll('.bundle-cart-step, .bundle-cart-features');
  }

  setMobileDrawer(expanded, animate = true) {
    if (!this.sidebarCart) return;

    const mobile = window.innerWidth < 1024;
    if (!mobile) {
      this.sidebarCart.classList.remove('expanded');
      this.drawerPanels().forEach((panel) => {
        panel.style.height = '';
      });
      return;
    }

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.sidebarCart.classList.toggle('expanded', expanded);

    this.drawerPanels().forEach((panel) => {
      if (panel._drawerFinish) {
        panel.removeEventListener('transitionend', panel._drawerFinish);
        panel._drawerFinish = null;
      }

      if (!animate || reduceMotion) {
        panel.style.height = expanded ? 'auto' : '0px';
        return;
      }

      if (expanded) {
        const from = panel.getBoundingClientRect().height;
        panel.style.height = 'auto';
        const to = panel.scrollHeight;
        panel.style.height = `${from}px`;
        panel.offsetHeight;
        panel.style.height = `${to}px`;
        const finishOpen = (event) => {
          if (event.propertyName !== 'height' || event.target !== panel) return;
          panel.removeEventListener('transitionend', finishOpen);
          panel._drawerFinish = null;
          if (this.sidebarCart.classList.contains('expanded')) panel.style.height = 'auto';
        };
        panel._drawerFinish = finishOpen;
        panel.addEventListener('transitionend', finishOpen);
        return;
      }

      panel.style.height = `${panel.scrollHeight}px`;
      panel.offsetHeight;
      panel.style.height = '0px';
    });
  }

  initMobileSidebar() {
    if (!this.sidebarCart) return;

    const header = this.sidebarCart.querySelector('.bundle-cart-header');
    this.setMobileDrawer(false, false);

    if (header) {
      header.addEventListener('click', () => {
        if (window.innerWidth >= 1024) return;
        const opening = !this.sidebarCart.classList.contains('expanded');
        this.setMobileDrawer(opening, true);
      });
    }

    window.addEventListener('resize', () => {
      if (window.innerWidth >= 1024) this.setMobileDrawer(false, false);
    });

    document.addEventListener('click', (event) => {
      if (window.innerWidth >= 1024 || !this.sidebarCart.classList.contains('expanded')) return;
      if (!this.sidebarCart.contains(event.target)) this.setMobileDrawer(false, true);
    });

    const inner = this.sidebarCart.querySelector('.bundle-sidebar-cart-inner');
    if (inner) {
      inner.addEventListener('click', (event) => event.stopPropagation());
    }
  }

  initCtaCloseSidebar() {
    const cta = this.querySelector('.bundle-cart-cta-button');
    if (!cta || !this.sidebarCart) return;

    cta.addEventListener('click', () => {
      this.setMobileDrawer(false, true);
    });
  }

  showAddedFeedback(button) {
    const existingTimeout = button.getAttribute('data-feedback-timeout');
    if (existingTimeout) clearTimeout(parseInt(existingTimeout, 10));

    const originalHTML = button.getAttribute('data-original-html') || button.innerHTML;
    if (!button.hasAttribute('data-original-html')) {
      button.setAttribute('data-original-html', originalHTML);
    }

    button.setAttribute('data-showing-feedback', 'true');
    button.innerHTML = `
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M22.8084 6.3975L20.5584 4.18875C20.2773 3.90852 19.8965 3.75116 19.4995 3.75116C19.1026 3.75116 18.7218 3.90852 18.4406 4.18875L9.75095 12.7716L6.31126 9.43406C6.02952 9.15474 5.64859 8.9984 5.25185 8.99928C4.85512 9.00016 4.47488 9.15818 4.19438 9.43875L1.94438 11.6887C1.66358 11.97 1.50586 12.3512 1.50586 12.7486C1.50586 13.146 1.66358 13.5272 1.94438 13.8084L8.65876 20.5584C8.79805 20.6978 8.96343 20.8083 9.14544 20.8837C9.32745 20.9591 9.52253 20.9979 9.71954 20.9979C9.91655 20.9979 10.1116 20.9591 10.2936 20.8837C10.4757 20.8083 10.641 20.6978 10.7803 20.5584L22.8131 8.52281C22.9527 8.38314 23.0633 8.21729 23.1387 8.03476C23.214 7.85224 23.2526 7.65664 23.2521 7.45918C23.2517 7.26172 23.2123 7.06629 23.1361 6.8841C23.06 6.70191 22.9486 6.53655 22.8084 6.3975ZM9.71532 19.5L3.00095 12.75L5.25095 10.5C5.25368 10.5023 5.25619 10.5048 5.25845 10.5075L9.22876 14.3597C9.36891 14.4968 9.5572 14.5736 9.75329 14.5736C9.94938 14.5736 10.1377 14.4968 10.2778 14.3597L19.5066 5.25L21.7509 7.4625L9.71532 19.5Z" fill="white"/>
      </svg>
    `;
    button.style.background = '#15A61A';
    button.style.color = '#fff';
    button.style.borderColor = '#15A61A';

    const timeoutId = setTimeout(() => {
      button.innerHTML = originalHTML;
      button.style.background = '';
      button.style.color = '';
      button.style.borderColor = '';
      button.removeAttribute('data-showing-feedback');
      button.removeAttribute('data-feedback-timeout');
      this.updateAddButtonsState();
    }, 2000);

    button.setAttribute('data-feedback-timeout', timeoutId.toString());
  }

  async fetchCartFresh(ttlMs = 2000) {
    const now = Date.now();
    if (this._cartCache.cart && (now - this._cartCache.ts) < ttlMs) {
      return this._cartCache.cart;
    }

    try {
      const res = await fetch(`${this.routesRoot()}cart.js?t=${now}`, { credentials: 'same-origin' });
      if (!res.ok) throw new Error('cart fetch failed');
      const cart = await res.json();
      this._cartCache = { ts: now, cart };
      return cart;
    } catch (e) {
      return null;
    }
  }

  getVariantQtyInCart(cart, variantId) {
    if (!cart || !Array.isArray(cart.items)) return 0;
    const idNum = Number(variantId);
    let qty = 0;
    cart.items.forEach((it) => {
      if (Number(it.variant_id) === idNum) qty += (it.quantity || 0);
    });
    return qty;
  }

  async fetchVariantInventoryLimit(variantId, fallbackInventoryAttr, ttlMs = 10000) {
    const now = Date.now();
    const cached = this._variantInventoryCache.get(String(variantId));
    if (cached && (now - cached.ts) < ttlMs) return cached;

    const fallbackHasLimit = this.hasInventoryLimit(fallbackInventoryAttr);
    const fallbackMax = this.getEffectiveMaxInventory(fallbackInventoryAttr);

    try {
      const res = await fetch(`${this.routesRoot()}variants/${variantId}.json?t=${now}`, { credentials: 'same-origin' });
      if (!res.ok) throw new Error('variant fetch failed');

      const json = await res.json();
      const v = json && (json.variant || json);
      const inventoryPolicy = v?.inventory_policy;
      const inventoryManagement = v?.inventory_management;
      const invQty = v?.inventory_quantity;

      const fetchedHasLimit =
        inventoryManagement != null &&
        inventoryPolicy !== 'continue' &&
        typeof invQty === 'number' &&
        invQty >= 0;

      const fetchedMax = fetchedHasLimit ? invQty : 999999;
      const hasLimit = fallbackHasLimit || fetchedHasLimit;

      let maxInventory = 999999;
      if (fallbackHasLimit && fetchedHasLimit) {
        maxInventory = Math.min(fallbackMax, fetchedMax);
      } else if (fallbackHasLimit) {
        maxInventory = fallbackMax;
      } else if (fetchedHasLimit) {
        maxInventory = fetchedMax;
      }

      const result = { ts: now, hasLimit, maxInventory };
      this._variantInventoryCache.set(String(variantId), result);
      return result;
    } catch (e) {
      const result = { ts: now, hasLimit: fallbackHasLimit, maxInventory: fallbackMax };
      this._variantInventoryCache.set(String(variantId), result);
      return result;
    }
  }
}

if (!window.customElements.get('trial-builder')) {
  customElements.define('trial-builder', TrialBuilder);
}
